import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type {
  AssociationType,
  CrmObjectTypeSchema,
} from '@/lib/crm/core/SchemaClient';
import {
  entityTypesQueryKey,
  fetchEntityTypeSchemas,
} from '@/lib/crm/hooks/useEntityTypeSchema';
import {
  fetchAllPropertyTypes,
  type FieldTypeSchema,
} from '@/lib/crm/hooks/useFilterSchema';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { resolveAssociationTargetTypes } from '@/lib/crm/utils/associationMatching';

import { mergeAssociatedTargetProperties } from './mergeTargetProperties';

interface UseAssociatedPropertySchemasResult {
  isObjectTypesError: boolean;
  objectTypeLookup: Record<string, CrmObjectTypeSchema>;
  resolvedTargetTypes: Record<string, string[]>;
  targetFilterableProps: Record<string, PropertyTypeSchema[]>;
  targetFetchErrors: Set<string>;
  /** Per-association target property lookup maps for FilterConditionRow. */
  targetPropertyMaps: Record<string, Record<string, PropertyTypeSchema>>;
}

/**
 * Resolves each filterable association's concrete target entity type and
 * fetches that target's filterable property schemas, so the field combobox
 * can flatten association properties in one step (e.g. "Industry  Works At").
 * Only fetches when `enableAssociatedPropertyFilters` is true.
 */
export function useAssociatedPropertySchemas(
  teamId: number,
  enableAssociatedPropertyFilters: boolean,
  filterableAssociations: AssociationType[],
  fieldTypeMap: Record<string, FieldTypeSchema>
): UseAssociatedPropertySchemasResult {
  // Fetch entity type schemas so we can:
  // 1. Resolve kind-based association target types
  // 2. Get human labels for target entity types ("company", "deal")
  const { data: objectTypesData, isError: isObjectTypesError } = useQuery({
    queryKey: entityTypesQueryKey(teamId),
    queryFn: () => fetchEntityTypeSchemas(teamId),
    enabled:
      !!teamId &&
      enableAssociatedPropertyFilters &&
      filterableAssociations.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Build a lookup from entity_type_name → CrmObjectTypeSchema for label access
  const objectTypeLookup = useMemo(() => {
    const map: Record<string, CrmObjectTypeSchema> = {};
    if (objectTypesData) {
      for (const ot of objectTypesData) {
        map[ot.entity_type_name] = ot;
      }
    }
    return map;
  }, [objectTypesData]);

  // Resolve each association's concrete target entity type name(s).
  const resolvedTargetTypes = useMemo(() => {
    if (!enableAssociatedPropertyFilters) {
      return {};
    }
    const map: Record<string, string[]> = {};
    for (const assoc of filterableAssociations) {
      const targetTypes = resolveAssociationTargetTypes(assoc, objectTypesData);
      if (targetTypes.length > 0) {
        map[assoc.association_type_name] = targetTypes;
      }
    }
    return map;
  }, [
    enableAssociatedPropertyFilters,
    filterableAssociations,
    objectTypesData,
  ]);

  // Dedupe target entity types for batch property fetching.
  const uniqueTargetTypes = useMemo(
    () => [...new Set(Object.values(resolvedTargetTypes).flat())],
    [resolvedTargetTypes]
  );

  // Fetch property schemas for ALL entity types in one unfiltered request and
  // group client-side. A per-target-type useQueries fan-out here fired one
  // request per associated entity type on every slideout open, exhausting the
  // shared per-org rate budget (org-wide 429s).
  const {
    data: allPropertyTypes,
    isError: isTargetPropsError,
    error: targetPropsError,
  } = useQuery({
    queryKey: ['filterSchema', 'allPropertyTypes', String(teamId)],
    queryFn: () => fetchAllPropertyTypes(teamId),
    enabled:
      !!teamId &&
      enableAssociatedPropertyFilters &&
      uniqueTargetTypes.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Build a map: targetEntityType → filterable PropertyTypeSchema[]
  const targetFilterableProps = useMemo(() => {
    const map: Record<string, PropertyTypeSchema[]> = {};
    if (!allPropertyTypes) {
      return map;
    }
    const grouped: Record<string, PropertyTypeSchema[]> = {};
    for (const prop of allPropertyTypes) {
      (grouped[prop.entity_type_name] ??= []).push(prop);
    }
    for (const targetType of uniqueTargetTypes) {
      map[targetType] = (grouped[targetType] ?? [])
        .filter(prop => {
          if (prop.value_read_access === 'internal') {
            return false;
          }
          const ft = fieldTypeMap[prop.field_type_name];
          return ft && ft.operators.length > 0;
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    return map;
  }, [allPropertyTypes, uniqueTargetTypes, fieldTypeMap]);

  // Track which target types failed to load so we can surface an error item
  // in the combobox instead of silently showing an empty section. With the
  // single batched fetch, a failure affects every target type at once.
  const targetFetchErrors = useMemo(() => {
    const errors = new Set<string>();
    if (isTargetPropsError) {
      for (const targetType of uniqueTargetTypes) {
        errors.add(targetType);
      }
      console.error(
        '[SchemaFilterBuilder] Failed to fetch target property schemas:',
        targetPropsError
      );
    }
    return errors;
  }, [isTargetPropsError, targetPropsError, uniqueTargetTypes]);

  // Build per-association target property lookup maps for FilterConditionRow.
  const targetPropertyMaps = useMemo(() => {
    const maps: Record<string, Record<string, PropertyTypeSchema>> = {};
    for (const assoc of filterableAssociations) {
      const targetTypes =
        resolvedTargetTypes[assoc.association_type_name] ?? [];
      const merged = mergeAssociatedTargetProperties(
        targetTypes,
        targetFilterableProps
      );
      if (Object.keys(merged).length > 0) {
        maps[assoc.association_type_name] = merged;
      }
    }
    return maps;
  }, [filterableAssociations, resolvedTargetTypes, targetFilterableProps]);

  return {
    isObjectTypesError,
    objectTypeLookup,
    resolvedTargetTypes,
    targetFilterableProps,
    targetFetchErrors,
    targetPropertyMaps,
  };
}
